import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, FileText, MessagesSquare, Users, UsersRound } from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import { EmptyState } from '../ui/EmptyState';
import { SectionHeader } from '../ui/SectionHeader';
import { SkeletonLoader } from '../SkeletonLoader';
import { PartnerAvatar } from './PartnerAvatar';
import { PartnerPhaseBadge, PartnerTierBadge } from './PartnerPhaseBadge';
import { PartnerSyncStatus } from './PartnerSyncStatus';
import type { PartnerDetail } from '../../types/partners';
import { memberName, partnerPagePath } from '../../utils/partnerLinks';
import { isAccessIssue } from '../../utils/partnerStatus';

interface PartnerSummaryCardProps {
  creatorId: string;
}

/**
 * The partner team behind a partnership, in brief: phase, Discord channel,
 * team and open update reports, with the way into the partner page where all
 * of it is managed.
 */
export const PartnerSummaryCard = ({ creatorId }: PartnerSummaryCardProps) => {
  const [detail, setDetail] = useState<PartnerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    partnersApi
      .get(creatorId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'The partner could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [creatorId]);

  if (loading) {
    return (
      <div className="space-y-3" aria-busy="true">
        <SkeletonLoader className="h-5 w-48" />
        <SkeletonLoader className="h-16" />
        <SkeletonLoader className="h-24" />
      </div>
    );
  }

  if (error || !detail) {
    return <EmptyState bare compact tone="danger" icon={AlertCircle} title="The partner could not be loaded" description={error ?? undefined} />;
  }

  const active = detail.members.filter((m) => m.status === 'active');
  const developers = active.filter((m) => m.tier === 'developer').length;
  const issues = active.filter((m) => isAccessIssue(m.access_state)).length;

  return (
    <div className="space-y-4">
      <SectionHeader
        title={detail.creator.name}
        icon={UsersRound}
        description="The partner team this partnership belongs to. One team and one channel cover all of their partnered packs."
        actions={
          <Link to={partnerPagePath(creatorId)} className="btn-primary">
            Open partner page
            <ArrowRight className="w-4 h-4" />
          </Link>
        }
      />

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <PartnerPhaseBadge phase={detail.phase} reason={detail.phase_reason} />
        <span className="text-glass-400">{detail.phase_reason}</span>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="subcard p-3 space-y-2">
          <p className="eyebrow flex items-center gap-1.5">
            <MessagesSquare className="w-3.5 h-3.5" /> Discord
          </p>
          <PartnerSyncStatus discord={detail.discord} now={Date.now()} />
          <Link to={partnerPagePath(creatorId, 'discord')} className="link text-xs inline-flex items-center gap-1">
            Manage the channel <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="subcard p-3 space-y-2">
          <p className="eyebrow flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Team
          </p>
          <p className="text-sm text-glass-300 tabular-nums">
            {active.length === 0
              ? 'Nobody is on the team yet.'
              : `${developers} Partnered Developer${developers === 1 ? '' : 's'}, ${active.length - developers} Contributor${active.length - developers === 1 ? '' : 's'}.`}
            {issues > 0 && <span className="text-amber-300"> {issues} need{issues === 1 ? 's' : ''} a look.</span>}
          </p>
          {active.length > 0 && (
            <ul className="space-y-1.5">
              {active.slice(0, 6).map((m) => (
                <li key={m.id} className="flex items-center gap-2 min-w-0">
                  <PartnerAvatar avatarUrl={m.discord_avatar_url} name={memberName(m)} />
                  <span className="text-sm text-white truncate flex-1">{memberName(m)}</span>
                  <PartnerTierBadge tier={m.tier} />
                </li>
              ))}
              {active.length > 6 && <li className="text-xs text-glass-500">and {active.length - 6} more</li>}
            </ul>
          )}
          <Link to={partnerPagePath(creatorId, 'team')} className="link text-xs inline-flex items-center gap-1">
            {active.length ? 'Manage the team' : 'Add the team'} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      <div className="subcard p-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-glass-300 flex items-center gap-2">
          <FileText className="w-4 h-4 text-glass-400" />
          {detail.open_updates === 0
            ? 'No update reports are waiting on staff.'
            : `${detail.open_updates} update report${detail.open_updates === 1 ? ' is' : 's are'} waiting on staff.`}
        </p>
        <Link to={partnerPagePath(creatorId, 'updates')} className="link text-xs inline-flex items-center gap-1">
          See update reports <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
};
